import { useEffect, useState, useCallback } from "react";
import { FileDown, FileText, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand } from "@/context/CommandContext";
import { Panel, CCButton, LoadingState, KpiCard } from "@/command/ccUi";

export default function Reports() {
  const { can } = useCommand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [withSummary, setWithSummary] = useState(true);
  const [narratives, setNarratives] = useState({});
  const canExport = can("reports", "EXPORT") || can("reports", "MANAGE");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cc/reports").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportReport = async (key, format) => {
    setBusy(`${key}-${format}`);
    try {
      const nParam = format === "pdf" && withSummary ? "&narrative=true" : "";
      const res = await api.get(`/cc/reports/export/${key}?format=${format}${nParam}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${key}.${format}`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Export failed"); }
    finally { setBusy(""); }
  };

  const generate = async (key) => {
    setBusy(`${key}-ai`);
    try {
      const { data } = await api.post("/cc/reports/narrative", { report: key });
      setNarratives((n) => ({ ...n, [key]: data.narrative }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "AI summary failed"); }
    finally { setBusy(""); }
  };

  if (loading) return <LoadingState label="Loading reports…" />;
  const h = data?.highlights || {};

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Reports</h1>
      <p className="text-subtle mt-1 mb-6">Cross-module highlights with AI narratives and CSV / PDF export.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Revenue" value={h.revenue} prefix="₹" />
        <KpiCard label="Net Cash" value={h.net_cash} prefix="₹" />
        <KpiCard label="Pipeline" value={h.pipeline} prefix="₹" />
        <KpiCard label="Headcount" value={h.headcount} />
        <KpiCard label="Customers" value={h.customers} />
        <KpiCard label="Expenses" value={h.expenses} prefix="₹" />
        <KpiCard label="Compliance Overdue" value={h.compliance_overdue} />
        <KpiCard label="Reports" value={data?.available?.length || 0} />
      </div>

      <Panel title="Exportable reports" testid="reports-panel"
        action={
          <label className="flex items-center gap-2 text-xs text-subtle" data-testid="reports-include-summary">
            <input type="checkbox" checked={withSummary} onChange={(e) => setWithSummary(e.target.checked)} />
            Include AI summary in PDF
          </label>
        }>
        <div className="flex flex-col gap-3">
          {data?.available?.map((r) => (
            <div key={r.key} className="p-4 rounded-xl glass-medium" data-testid={`report-${r.key}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-subtle" />
                  <div><p className="text-ink font-medium">{r.title}</p><p className="text-xs text-subtle">{r.count} record(s)</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <CCButton variant="ghost" onClick={() => generate(r.key)} disabled={busy} testid={`narrative-${r.key}`}>
                    {busy === `${r.key}-ai` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-oraya-accent" />} AI summary
                  </CCButton>
                  <CCButton variant="ghost" onClick={() => exportReport(r.key, "csv")} disabled={!canExport || busy} testid={`export-csv-${r.key}`}>
                    {busy === `${r.key}-csv` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} CSV
                  </CCButton>
                  <CCButton variant="ghost" onClick={() => exportReport(r.key, "pdf")} disabled={!canExport || busy} testid={`export-pdf-${r.key}`}>
                    {busy === `${r.key}-pdf` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF
                  </CCButton>
                </div>
              </div>
              {narratives[r.key] && (
                <div className="mt-3 pt-3 border-t border-hairline/[0.08]" data-testid={`narrative-text-${r.key}`}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-subtle mb-2"><Sparkles className="w-3 h-3 text-oraya-accent" /> AI Executive Summary</div>
                  <div className="text-sm text-ink whitespace-pre-line leading-relaxed">{narratives[r.key]}</div>
                </div>
              )}
            </div>
          ))}
        </div>
        {!canExport && <p className="text-xs text-subtle mt-4">You don't have export permission for reports.</p>}
      </Panel>
    </div>
  );
}
